/* BTW (VAT) calculation utility functions
   Created: Added utility functions to calculate prices with BTW
   Updated: Added calculatePriceExclBTW function for reverse calculation from incl to excl BTW
   Updated: Added calculateBTWBreakdown helper to group incl-BTW totals by per-line BTW
   percentage for mixed-rate invoices (0% / 9% / 21%).
   Updated: Customer-facing prices including VAT round up to the nearest 5 cents; excl. VAT values remain unchanged. */

export interface PriceWithBTW {
  priceExclBTW: number;
  priceInclBTW: number;
  btwAmount: number;
  btwPercentage: number;
}

/**
 * Calculate price including BTW
 * @param priceExclBTW - Price excluding BTW
 * @param btwPercentage - BTW percentage (default 9%)
 * @returns Price including BTW, rounded to 2 decimal places
 */
export function calculatePriceInclBTW(priceExclBTW: number, btwPercentage: number = 9): number {
  const priceInclBTW = priceExclBTW * (1 + btwPercentage / 100);
  return Math.ceil(priceInclBTW * 20) / 20;
}

/**
 * Calculate price excluding BTW from price including BTW
 * @param priceInclBTW - Price including BTW
 * @param btwPercentage - BTW percentage (default 9%)
 * @returns Price excluding BTW, rounded to 2 decimal places
 */
export function calculatePriceExclBTW(priceInclBTW: number, btwPercentage: number = 9): number {
  const priceExclBTW = priceInclBTW / (1 + btwPercentage / 100);
  return Math.round(priceExclBTW * 100) / 100;
}

/**
 * Calculate detailed BTW information
 * @param priceExclBTW - Price excluding BTW
 * @param btwPercentage - BTW percentage (default 9%)
 * @returns Object with detailed price and BTW information, rounded to 2 decimal places
 */
export function calculateBTWDetails(priceExclBTW: number, btwPercentage: number = 9): PriceWithBTW {
  const priceInclBTW = calculatePriceInclBTW(priceExclBTW, btwPercentage);
  const btwAmount = Math.round((priceInclBTW - priceExclBTW) * 100) / 100;

  return {
    priceExclBTW: Math.round(priceExclBTW * 100) / 100,
    priceInclBTW,
    btwAmount,
    btwPercentage,
  };
}

/**
 * Format price for display
 * @param price - Price to format
 * @param includeCurrency - Whether to include currency symbol (default true)
 * @returns Formatted price string
 */
export function formatPrice(price: number, includeCurrency: boolean = true): string {
  const formatted = price.toFixed(2);
  return includeCurrency ? `€${formatted}` : formatted;
}

/**
 * Calculate graduated pricing with BTW
 * @param quantity - Order quantity
 * @param priceTier1 - Price for 0-25 items (excl BTW)
 * @param priceTier2 - Price for 26-50 items (excl BTW)
 * @param priceTier3 - Price for 51-100 items (excl BTW)
 * @param priceTier4 - Price for 101+ items (excl BTW)
 * @param btwPercentage - BTW percentage (default 9%)
 * @returns Price per item including BTW based on quantity
 */
export function calculateGraduatedPriceInclBTW(
  quantity: number,
  priceTier1: number,
  priceTier2: number | null,
  priceTier3: number | null,
  priceTier4: number | null,
  btwPercentage: number = 9
): number {
  let priceExclBTW: number;

  if (quantity >= 101 && priceTier4) {
    priceExclBTW = priceTier4;
  } else if (quantity >= 51 && priceTier3) {
    priceExclBTW = priceTier3;
  } else if (quantity >= 26 && priceTier2) {
    priceExclBTW = priceTier2;
  } else {
    priceExclBTW = priceTier1;
  }

  return calculatePriceInclBTW(priceExclBTW, btwPercentage);
}

export interface BTWBreakdownEntry {
  percentage: number;
  exclBTW: number;
  btwAmount: number;
  inclBTW: number;
}

export interface BTWBreakdown {
  entries: BTWBreakdownEntry[];
  totalExclBTW: number;
  totalBTWAmount: number;
  totalInclBTW: number;
}

/**
 * Group incl-BTW line totals by BTW percentage and return per-rate and
 * aggregate excl / btw / incl amounts. Used by invoices/quotes to show a
 * correct breakdown when lines mix 0%, 9% and 21% BTW rates.
 */
export function calculateBTWBreakdown(
  lines: { totalInclBTW: number; btwPercentage: number }[]
): BTWBreakdown {
  const grouped = new Map<number, number>();
  for (const line of lines) {
    const pct = Number(line.btwPercentage) || 0;
    grouped.set(pct, (grouped.get(pct) || 0) + line.totalInclBTW);
  }

  const entries: BTWBreakdownEntry[] = [];
  let totalExclBTW = 0;
  let totalBTWAmount = 0;
  let totalInclBTW = 0;

  const sortedPcts = Array.from(grouped.keys()).sort((a, b) => a - b);
  for (const pct of sortedPcts) {
    const inclBTW = Math.round((grouped.get(pct) || 0) * 100) / 100;
    const exclBTW = Math.round((inclBTW / (1 + pct / 100)) * 100) / 100;
    const btwAmount = Math.round((inclBTW - exclBTW) * 100) / 100;
    entries.push({ percentage: pct, exclBTW, btwAmount, inclBTW });
    totalExclBTW += exclBTW;
    totalBTWAmount += btwAmount;
    totalInclBTW += inclBTW;
  }

  return {
    entries,
    totalExclBTW: Math.round(totalExclBTW * 100) / 100,
    totalBTWAmount: Math.round(totalBTWAmount * 100) / 100,
    totalInclBTW: Math.round(totalInclBTW * 100) / 100,
  };
}
